"use client";
import {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useCallback,
} from "react";
import { Locale } from "@/dto/locale";
import { getCurrencyCodeFromCountry } from "@/utils/currencyUtils";
import { useFormatter } from "next-intl";
import { Countries } from "country-to-currency";

interface CurrencyContextValue {
  locale: Locale;
  currency: string;
  // eslint-disable-next-line no-unused-vars
  format: (amount: number, minimumFractionDigits?: number) => string;
}
const CurrencyContext = createContext<CurrencyContextValue | undefined>(
  undefined
);
export function CurrencyProvider({
  locale = "en-US",
  currency: explicitCurrency,
  children,
}: Readonly<{
  locale: Locale;
  currency?: string;
  children: ReactNode;
}>) {
  const formatter = useFormatter();

  const activeCurrency = useMemo(() => {
    if (explicitCurrency) return explicitCurrency;

    const [, country] =
      typeof locale === "string" ? locale.split("-") : ["", ""];

    return getCurrencyCodeFromCountry(country as Countries);
  }, [explicitCurrency, locale]);

  const format = useCallback(
    (amount: number, minimumFractionDigits: number = 2) => {
      return formatter.number(amount / 100, {
        style: "currency",
        currency: activeCurrency,
        minimumFractionDigits,
        currencyDisplay: "symbol",
      });
    },
    [formatter, activeCurrency]
  );

  const value = useMemo(
    () => ({ locale, currency: activeCurrency, format }),
    [locale, activeCurrency, format]
  );

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be inside CurrencyProvider");
  return ctx;
}
